import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Heart } from "lucide-react";
import { Section, Container, Eyebrow, DisplayH1, DisplayH2, Reveal } from "@/components/Layout";
import { api } from "@/lib/api";

const CAUSES = [
  { slug: "veterans", name: "Veterans Program", desc: "Service dogs and training scholarships for veterans and first responders." },
  { slug: "service-dogs", name: "Service Dogs for Families", desc: "Fund the raising and matching of medical and psychiatric service dogs." },
  { slug: "youth", name: "Youth Trainers Fund", desc: "Scholarships for the Junior Trainer Academy for students 12–17." },
  { slug: "sponsor-dog", name: "Sponsor a Dog", desc: "Support a specific dog through their full 18-month training journey." },
];

const AMOUNTS = [50, 250, 1000, 5000];

export default function Foundation() {
  const [form, setForm] = useState({ name: "", email: "", amount: 250, cause: "veterans", message: "" });
  const [busy, setBusy] = useState(false);
  const [dogs, setDogs] = useState([]);
  useEffect(() => { api.get("/foundation/dogs").then((r) => setDogs((r.data.dogs || []).slice(0, 3))); }, []);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/donations/pledge", { ...form, amount: Number(form.amount) }); toast.success("Your pledge is recorded. We will follow up shortly."); setForm({ ...form, name: "", email: "", message: "" }); }
    catch { toast.error("Could not record your pledge."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Section testid="foundation-hero" className="pt-40 pb-16 relative overflow-hidden">
        <Container>
          <Eyebrow>Golden Wolf Foundation</Eyebrow>
          <DisplayH1 className="mt-6 max-w-4xl">Fund the next generation of<br /><span className="text-[color:var(--wk-gold)]">working, service, and family dogs.</span></DisplayH1>
          <p className="mt-8 max-w-2xl text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
            The Golden Wolf Foundation places trained dogs with veterans, first responders,
            children, and families in need — at no cost to the recipient.
          </p>
        </Container>
      </Section>

      <Section testid="foundation-causes" className="pb-16">
        <Container>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {CAUSES.map((c) => (
              <button
                key={c.slug}
                data-testid={`cause-${c.slug}`}
                onClick={() => set("cause", c.slug)}
                className={`text-left card-lux p-8 h-full transition-all ${form.cause === c.slug ? "border-[color:var(--wk-gold)]/60 -translate-y-1" : ""}`}
              >
                <div className="font-serif-display text-2xl">{c.name}</div>
                <p className="mt-3 text-sm text-[color:var(--wk-bone-dim)] leading-relaxed">{c.desc}</p>
              </button>
            ))}
          </div>
        </Container>
      </Section>

      {/* Sponsor a Dog feature */}
      <Section testid="foundation-dogs-feature" className="py-24 border-y border-white/10">
        <Container>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <Eyebrow>Sponsor a Dog</Eyebrow>
              <DisplayH2 className="mt-4 max-w-2xl">Meet the dogs whose stories your gift will shape.</DisplayH2>
            </div>
            <Link to="/foundation/dogs" className="btn-ghost h-11 shrink-0" data-testid="foundation-see-all-dogs">
              Meet the whole pack <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {dogs.map((d) => {
              const pct = Math.min(100, Math.round((d.raised_usd / d.goal_usd) * 100));
              return (
                <Reveal key={d.dog_id}>
                  <Link to={`/foundation/dogs/${d.dog_id}`} className="card-lux overflow-hidden block h-full group">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
                      <div className="absolute inset-0 cinema-overlay" />
                      <div className="absolute bottom-3 left-4 right-4">
                        <div className="font-serif-display text-3xl">{d.name}</div>
                        <div className="text-xs text-[color:var(--wk-bone-dim)]">{d.breed} · {d.stage}</div>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-[color:var(--wk-gold)]" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-[color:var(--wk-bone-dim)]">
                        <span className="text-[color:var(--wk-bone)]">${d.raised_usd.toLocaleString()} of ${d.goal_usd.toLocaleString()}</span>
                        <span className="flex items-center gap-1"><Heart size={11} className="text-[color:var(--wk-gold)]" /> {d.sponsors}</span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </Section>

      <Section testid="foundation-form" className="pb-32">
        <Container>
          <div className="card-lux p-10 max-w-3xl">
            <Eyebrow>Pledge your support</Eyebrow>
            <div className="mt-3 font-serif-display text-3xl">Cause: <span className="text-[color:var(--wk-gold)]">{CAUSES.find((c) => c.slug === form.cause)?.name}</span></div>
            <form onSubmit={submit} className="mt-8 space-y-6">
              <div className="flex flex-wrap gap-3">
                {AMOUNTS.map((a) => (
                  <button
                    type="button"
                    key={a}
                    onClick={() => set("amount", a)}
                    data-testid={`donation-amount-${a}`}
                    className={`px-6 py-3 rounded-full text-sm border ${form.amount === a ? "bg-[color:var(--wk-gold)] text-black border-[color:var(--wk-gold)]" : "border-white/20 hover:border-[color:var(--wk-gold)]"}`}
                  >
                    ${a.toLocaleString()}
                  </button>
                ))}
                <input type="number" min="1" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="w-32 bg-transparent border border-white/20 rounded-full px-4 py-3 text-sm outline-none focus:border-[color:var(--wk-gold)]" data-testid="donation-amount-custom" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="overline">Full Name</label>
                  <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-lg" data-testid="donation-name" />
                </div>
                <div>
                  <label className="overline">Email</label>
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-lg" data-testid="donation-email" />
                </div>
              </div>
              <div>
                <label className="overline">Message (optional)</label>
                <textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-base resize-none" />
              </div>
              <div className="text-xs text-[color:var(--wk-bone-dim)]">Note: This is a pledge form. Payment processing will be activated in the next phase.</div>
              <button disabled={busy} className="btn-gold" data-testid="donation-submit">{busy ? "Recording…" : "Pledge my support"}</button>
            </form>
          </div>
        </Container>
      </Section>
    </>
  );
}
